import React, { useContext } from 'react'
import { Box } from '@hub/box'
import { CoreContainer } from '@hub/core-container'
import { Text } from '@hub/text'
import { Portal } from '@hub/portal'
import { LinkContext } from '../../../context'
import analyticsUtils from '../../../helpers/analytics-utils'
import trackEvent from '../../../helpers/analytics-utils/track-event'
import { LinkItem, MegaMenuFeature } from '../types'
import { MegaMenuFeaturedItem } from './megamenu-featured-item'
import { MainNavItemWrapper } from './main-nav-item-wrapper'

const isLinkItem = (
  megamenuItem: MegaMenuFeature | LinkItem
): megamenuItem is LinkItem => 'label' in megamenuItem && 'url' in megamenuItem

const trackItemEvent = (parentLabel: string, itemLabel: string): void => {
  trackEvent(
    analyticsUtils.categories.UNIVERSAL_HEADER,
    analyticsUtils.actions.UNIVERSAL_HEADER_NAV_LINK_CLICK,
    `${parentLabel} - ${itemLabel}`
  )
}

export const MegaMenu: React.FC<{
  itemId: string
  itemLabel: string
  megaMenuItems?: (MegaMenuFeature | LinkItem)[]
}> = ({ itemId, itemLabel, megaMenuItems }) => {
  const Link = useContext(LinkContext)
  const featuredItemRef = React.useRef<HTMLElement | null>(null)

  return (
    <CoreContainer
      id={itemId}
      sx={{
        fontSize: 'font-sm',
        paddingY: 'spacing-md',
        display: 'flex',
        maxHeight: '480px',
        justifyContent: 'space-between',
      }}
    >
      <Box
        as="ul"
        id={itemId}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'wrap',
          alignContent: 'flex-start',
          flexBasis: '100%',
          overflow: 'hidden',
        }}
      >
        {megaMenuItems &&
          megaMenuItems.map((megamenuItem, i) => (
            <>
              {isLinkItem(megamenuItem) ? (
                <>
                  <MainNavItemWrapper
                    key={`${megamenuItem.url}-${megamenuItem.label}`}
                    sx={{
                      paddingTop: 'spacing-xs',
                      height: megamenuItem.fullColumn ? '100%' : 'revert',
                      width: '25%',
                      a: {
                        paddingBottom: 'spacing-1',
                        height: 'spacing-md',
                        display: 'block',
                        _hover: {
                          textDecoration: 'underline',
                        },
                      },
                      'ul li a': {
                        paddingBottom: 'initial',
                        height: 28,
                      },
                    }}
                  >
                    <Text
                      tone="strong"
                      as={props => (
                        <Link
                          {...props}
                          to={megamenuItem.url}
                          onClick={() =>
                            trackItemEvent(itemLabel, megamenuItem.label)
                          }
                        />
                      )}
                      id={`megamenu-item-${i}`}
                      lineHeight="none"
                    >
                      {megamenuItem.label}
                    </Text>
                    {megamenuItem.megaMenuItems && (
                      <Box as="ul">
                        {megamenuItem.megaMenuItems.map(subItem => (
                          <>
                            {isLinkItem(subItem) && (
                              <MainNavItemWrapper
                                key={`${subItem.url}-${subItem.label}`}
                              >
                                <Link
                                  key={`${subItem.url}-${subItem.label}`}
                                  to={subItem.url}
                                  component={({ children, ...props }) => (
                                    <a {...props}>{children}</a>
                                  )}
                                  onClick={() =>
                                    trackItemEvent(itemLabel, subItem.label)
                                  }
                                >
                                  {subItem.label}
                                </Link>
                              </MainNavItemWrapper>
                            )}
                          </>
                        ))}
                      </Box>
                    )}
                  </MainNavItemWrapper>
                </>
              ) : (
                <Portal containerRef={featuredItemRef}>
                  <MegaMenuFeaturedItem
                    parentCategory={itemLabel}
                    feature={megamenuItem}
                  />
                </Portal>
              )}
            </>
          ))}
      </Box>
      <Box
        ref={featuredItemRef}
        sx={{
          maxWidth: [0, 0, 0, '25%'],
          flexBasis: [0, 0, 0, '25%'],
        }}
      />
    </CoreContainer>
  )
}
